import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientService } from '../../api/services';
import { Client, CategorieClient, CATEGORIE_CLIENT_LABELS } from '../../types';
import { validateMatriculeFiscal, validateRIB } from '../../utils/validators';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

export function ClientForm() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Partial<Client>>({
    defaultValues: {
      categorie: CategorieClient.PERSONNE_MORALE,
      typeRelation: 'CLIENT',
      actif: true,
      exonere: false,
      delaiPaiement: 30,
      tauxRetenueSource: 0,
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      loadClient(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadClient = async (clientId: number) => {
    try {
      setLoading(true);
      const data = await clientService.getById(clientId);
      reset(data);
    } catch (error) {
      toast.error(t.messages.loadError);
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: Partial<Client>) => {
    try {
      setSaving(true);
      if (isEdit && id) {
        await clientService.update(parseInt(id), data);
      } else {
        await clientService.create(data);
      }
      toast.success(t.messages.saveSuccess);
      navigate('/clients');
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/clients')} className="btn-outline p-2">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="page-title">
            {isEdit ? t.clients.editClient : t.clients.newClient}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t.clients.raisonSociale}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t.common.code} *</label>
              <input
                {...register('code', { required: t.validation.required })}
                className={`input-field ${errors.code ? 'input-error' : ''}`}
                placeholder="CLT001"
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.clients.categorie} *</label>
              <select
                {...register('categorie', { required: t.validation.required })}
                className={`input-field ${errors.categorie ? 'input-error' : ''}`}
              >
                {Object.entries(CATEGORIE_CLIENT_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>


            <div className="md:col-span-2">
              <label className="label">{t.clients.raisonSociale} *</label>
              <input
                {...register('raisonSociale', {
                  required: t.validation.required,
                  minLength: { value: 2, message: 'Minimum 2 caractères' },
                })}
                className={`input-field ${errors.raisonSociale ? 'input-error' : ''}`}
              />
              {errors.raisonSociale && (
                <p className="mt-1 text-sm text-red-600">{errors.raisonSociale.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.clients.matriculeFiscal}</label>
              <input
                {...register('matriculeFiscal', {
                  validate: (value) => {
                    if (!value) return true;
                    return validateMatriculeFiscal(value) || t.validation.matriculeFiscal;
                  },
                })}
                className={`input-field font-mono ${errors.matriculeFiscal ? 'input-error' : ''}`}
                placeholder="1234567ABC123"
              />
              {errors.matriculeFiscal && (
                <p className="mt-1 text-sm text-red-600">{errors.matriculeFiscal.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">{t.clients.matriculeFormat}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t.clients.contact}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label">{t.common.adresse} *</label>
              <textarea
                {...register('adresse', { required: t.validation.required })}
                rows={2}
                className={`input-field ${errors.adresse ? 'input-error' : ''}`}
              />
              {errors.adresse && (
                <p className="mt-1 text-sm text-red-600">{errors.adresse.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.common.codePostal}</label>
              <input
                {...register('codePostal')}
                className="input-field"
                placeholder="1000"
              />
            </div>

            <div>
              <label className="label">{t.common.ville} *</label>
              <input
                {...register('ville', { required: t.validation.required })}
                className={`input-field ${errors.ville ? 'input-error' : ''}`}
                placeholder="Tunis"
              />
              {errors.ville && (
                <p className="mt-1 text-sm text-red-600">{errors.ville.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.common.telephone}</label>
              <input
                {...register('telephone')}
                className="input-field"
                placeholder="+216 71 123 456"
              />
            </div>

            <div>
              <label className="label">{t.clients.mobile}</label>
              <input
                {...register('mobile')}
                className="input-field"
                placeholder="+216 98 123 456"
              />
            </div>

            <div>
              <label className="label">{t.common.email}</label>
              <input
                type="email"
                {...register('email')}
                className="input-field"
                placeholder="contact@example.com"
              />
            </div>

            <div>
              <label className="label">{t.clients.fax}</label>
              <input
                {...register('fax')}
                className="input-field"
              />
            </div>

            <div>
              <label className="label">{t.clients.contactNom}</label>
              <input
                {...register('contactNom')}
                className="input-field"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informations bancaires et conditions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t.clients.rib}</label>
              <input
                {...register('rib', {
                  validate: (value) => {
                    if (!value) return true;
                    return validateRIB(value) || t.validation.rib;
                  },
                })}
                className={`input-field font-mono ${errors.rib ? 'input-error' : ''}`}
                placeholder="10 006 035 0000000001 97"
              />
              {errors.rib && (
                <p className="mt-1 text-sm text-red-600">{errors.rib.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t.clients.banque}</label>
              <input
                {...register('banque')}
                className="input-field"
              />
            </div>

            <div>
              <label className="label">{t.clients.plafondCredit}</label>
              <input
                type="number"
                step="0.001"
                {...register('plafondCredit', { valueAsNumber: true })}
                className="input-field"
              />
            </div>

            <div>
              <label className="label">{t.clients.delaiPaiement}</label>
              <input
                type="number"
                {...register('delaiPaiement', { valueAsNumber: true })}
                className="input-field"
              />
            </div>

            <div>
              <label className="label">{t.clients.tauxRetenueSource}</label>
              <input
                type="number"
                step="0.5"
                {...register('tauxRetenueSource', { valueAsNumber: true })}
                className="input-field"
              />
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('exonere')}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{t.clients.exonere}</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('actif')}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{t.clients.actif}</span>
            </label>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t.common.notes}</h2>
          <textarea
            {...register('notes')}
            rows={3}
            className="input-field"
            placeholder="Notes internes..."
          />
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/clients')}
            className="btn-outline"
          >
            {t.common.cancel}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary"
          >
            {saving ? t.common.loading : t.common.save}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ClientForm;
